/**
 * Render children only if the principal holds a permission.
 *
 * ```tsx
 * <Can permission="users.account.suspend">
 *   <SuspendButton />
 * </Can>
 * ```
 *
 * A convenience for keeping an interface coherent, not a security control —
 * see the note at the top of `snapshot.ts`. The server refuses the action
 * whatever this renders.
 *
 * Renders `fallback` (nothing, by default) while permissions load, so a user
 * never sees an admin control flash and disappear.
 */

import type { ReactNode } from "react";

import { useAccess } from "./context";

export interface CanProps {
  /** Required permission. Combine with `mode` for several. */
  permission?: string;
  permissions?: readonly string[];
  /** How several permissions combine. Defaults to requiring all. */
  mode?: "all" | "any";
  children: ReactNode;
  /** Shown when the permission is absent, and while loading. */
  fallback?: ReactNode;
}

export function Can({
  permission,
  permissions,
  mode = "all",
  children,
  fallback = null,
}: CanProps) {
  const { snapshot, ready } = useAccess();

  const required = permissions ?? (permission ? [permission] : []);
  if (required.length === 0) return <>{children}</>;
  if (!ready) return <>{fallback}</>;

  const allowed = mode === "any" ? snapshot.canAnyOf(required) : snapshot.canAllOf(required);
  return <>{allowed ? children : fallback}</>;
}
